import { Beaker, BookOpen, Brain, Github, type LucideIcon } from 'lucide-react';

import { type AdminFeedback } from '../../types/feedback';

type ActionMutation = { mutate: () => void; isPending: boolean };

const ActionButton = ({
  icon: Icon,
  tone,
  label,
  disabled,
  onClick,
}: {
  icon: LucideIcon;
  tone: string;
  label: string;
  disabled: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`flex w-full items-center gap-3 rounded-sm border px-4 py-3 text-sm font-medium transition-colors disabled:opacity-50 ${tone}`}
  >
    <Icon className="h-4 w-4" />
    {label}
  </button>
);

interface FeedbackActionsPanelProps {
  feedback: AdminFeedback;
  isRunning: boolean;
  triage: ActionMutation;
  knowledge: ActionMutation;
  evalCase: ActionMutation;
  github: ActionMutation;
}

export const FeedbackActionsPanel = ({
  feedback,
  isRunning,
  triage,
  knowledge,
  evalCase,
  github,
}: FeedbackActionsPanelProps) => {
  const isTriaged = feedback.status !== 'new';

  return (
    <div className="rounded-xl border border-black/10 bg-white p-6">
      <h3 className="text-sm font-semibold text-brand-blue mb-4">Åtgärder</h3>

      <div className="space-y-3">
        <ActionButton
          icon={Brain}
          tone="border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100"
          disabled={isRunning}
          onClick={() => triage.mutate()}
          label={
            triage.isPending ? 'Kör triage...' : isTriaged ? 'Kör om triage' : 'Kör OpenAI Triage'
          }
        />

        <ActionButton
          icon={BookOpen}
          tone="border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
          disabled={isRunning || feedback.status === 'knowledge_created'}
          onClick={() => knowledge.mutate()}
          label={
            knowledge.isPending
              ? 'Skapar...'
              : feedback.status === 'knowledge_created'
                ? 'Knowledge article skapad'
                : 'Skapa Knowledge Article'
          }
        />

        <ActionButton
          icon={Beaker}
          tone="border-teal-300 bg-teal-50 text-teal-700 hover:bg-teal-100"
          disabled={isRunning || feedback.status === 'eval_added'}
          onClick={() => evalCase.mutate()}
          label={
            evalCase.isPending
              ? 'Skapar...'
              : feedback.status === 'eval_added'
                ? 'Eval case skapad'
                : 'Lägg till Eval Case'
          }
        />

        <ActionButton
          icon={Github}
          tone="border-slate-300 bg-slate-50 text-slate-700 hover:bg-slate-100"
          disabled={
            isRunning || feedback.status === 'github_issue_created' || !!feedback.githubIssueUrl
          }
          onClick={() => github.mutate()}
          label={
            github.isPending
              ? 'Skapar...'
              : feedback.githubIssueUrl
                ? 'GitHub Issue finns'
                : 'Skapa GitHub Issue'
          }
        />

        {feedback.githubIssueUrl ? (
          <a
            href={feedback.githubIssueUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block overflow-hidden text-ellipsis rounded-sm border border-blue-300 bg-blue-50 px-4 py-2 text-xs text-blue-700 hover:underline"
          >
            {feedback.githubIssueUrl}
          </a>
        ) : null}
      </div>
    </div>
  );
};
